import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@dopomogai/ui';
import { useTranslation } from 'react-i18next';

interface AgentBuilderPageLayoutProps {
  children: React.ReactNode;
  onPrevious: () => void;
  onNext: () => void;
  onSave: () => void;
  isSaving?: boolean;
  isLastStep: boolean;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  showSave?: boolean;
}

export const AgentBuilderPageLayout: React.FC<AgentBuilderPageLayoutProps> = ({
  children,
  onPrevious,
  onNext,
  onSave,
  isSaving = false,
  isLastStep,
  canGoPrevious = true,
  canGoNext = true,
  showSave = false,
}) => {
  const { t } = useTranslation(['agentBuilder', 'common']);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const el = document.createElement('div');
    el.className = 'dg-agent-builder-command-bar fixed inset-x-0 bottom-0 z-[90] pointer-events-none';
    document.body.appendChild(el);
    setPortalContainer(el);

    return () => {
      document.body.removeChild(el);
    };
  }, []);

  const commandBar = useMemo(() => {
    if (!portalContainer) return null;

    const prevLabel = t('agentBuilder:actions.previous', 'Previous');
    const nextLabel = t('agentBuilder:actions.next', 'Next');
    const saveLabel = isSaving
      ? t('common:saving', 'Saving…')
      : t('agentBuilder:actions.save', 'Save & Activate');

    return createPortal(
      <div className="pointer-events-auto px-4 pb-4 md:px-6 md:pb-6">
        <div className="mx-auto max-w-5xl rounded-2xl border-2 border-black bg-white shadow-[6px_6px_0_0_#000] px-4 py-3 md:px-6 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              text={prevLabel}
              onClick={onPrevious}
              disabled={!canGoPrevious || isSaving}
            />
            {!isLastStep && (
              <Button
                variant="secondary"
                text={nextLabel}
                onClick={onNext}
                disabled={!canGoNext || isSaving}
              />
            )}
          </div>

          {showSave && (
            <Button
              variant="primary"
              text={saveLabel}
              onClick={onSave}
              disabled={isSaving}
            />
          )}
        </div>
      </div>,
      portalContainer
    );
  }, [portalContainer, onPrevious, onNext, onSave, canGoPrevious, canGoNext, isLastStep, isSaving, showSave, t]);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col pb-32">
      <main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-12">{children}</div>
      </main>
      {commandBar}
    </div>
  );
};